// Libs
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';

// Services & Helpers
import TextHelpers from 'helpers/TextHelpers';
import GlobalStateService from 'services/GlobalStateService';
import BookingService from 'services/BookingService';
import CourseService from 'services/CourseService';
import VendorService from 'services/VendorService';
import LocationService from 'services/LocationService';

// Components
import Loader from 'components/common/Loader';
import BookingsTable from 'components/bookings/BookingsTable';
import EditBookingModal from 'components/bookings/EditBookingModal';
import DatePicker from 'components/common/DatePicker';
import PageHeader from 'components/common/PageHeader';
import SideBar from 'components/common/SideBar';

//-------------------------------------------------------------------------------------------------------------------

class BookingsPage extends React.Component {

    constructor(props) {
        super(props);

        this.editBookingModalRef = React.createRef();

        this.requestID = 1;
        this.state = {
            isLoadingFilters: true,
            isLoading: true,
            isLoaded: false,
            bookings: [],
            isDirty: false,
            filters: {
                bookingType: '',
                status: '',
                orderBy: 'date',
                courseID: null,
                excludeShadow: false,
                includeNonATP: true,
                search: ''
            }
        };
    }
    
    componentDidMount() {
        window.scrollTo(0, 0);
        this.init();
    }

    componentDidUpdate(oldProps) {
        if (this.props.match.params.page != oldProps.match.params.page) {
            this.init();
        }
    }

    init() {
        GlobalStateService.setValues({
            area: 'bookings-' + (this.props.match.params.page || '')
        });
        this.loadFilters();
        this.loadMonthlyStats();
    }

    async loadFilters() {
        this.setState({ isLoadingFilters: true });
        const geos = await LocationService.listGeos();
        const vendors = await VendorService.list();
        const courses = await CourseService.list();

        this.setState({
            geos,
            vendors,
            courses,
            isLoadingFilters: false,
            isLoading: false
        });
    }
    
    async load() {
        const { filters } = this.state;
        const { page } = this.props.match.params;
        
        // Load bookings
        this.setState({ isLoading: true, isDirty: false });
        this.requestID++;
        const bookings = await BookingService.list({
            ...filters,
            status: (filters.status == 'Any' ? '' : filters.status)
        });
        this.loadMonthlyStats();

        // Update UI
        this.setState({
            bookings,
            isLoading: false,
            isLoaded: true
        });
    }

    async loadMonthlyStats() {
        const loginInfo = GlobalStateService.getValue('loginInfo');
        if (!loginInfo.isSiteAdmin) {
            return;
        }
        const { filters } = this.state;

        const monthlyStats = await BookingService.listMonthlyStats({
            ...filters
        });
        this.setState({ monthlyStats });
    }

    async exportToExcel() {
        const { filters } = this.state;
        await BookingService.excel({
            ...filters,
            status: (filters.status == 'Any' ? '' : filters.status)
        }, false);
    }

    updateFilter(name, value) {
        const filters = { ...this.state.filters };
        filters[name] = value;
        this.setState({
            filters,
            isDirty: true
        });
    }

    async editBooking(booking) {
        await this.editBookingModalRef.current.show({
            bookingID: booking.id
        });
        this.load();
    }

    async addBooking() {
        const { geoID, vendorID } = this.state.filters;
        await this.editBookingModalRef.current.show({
            status: 'Active',
            geoID,
            vendorID
        });
        this.load();
    }

    render() {
        const page = this.props.match.params.page || '';
        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (<>

            <div className="page bookings-page full-width-page">

                <PageHeader hasSideBar>
                    <h1>{page == 'archived' ? 'Archived' : page.toUpperCase()}{' '}Bookings</h1>

                    <div className="buttons">

                        {loginInfo.isAdmin &&
                            <button type="button" className="btn btn-primary" onClick={e => this.addBooking()}>
                                <span className="fa-solid fa-plus" />{' '}
                                New booking
                            </button>
                        }
                        
                        {loginInfo.site.enableExcelExport &&
                            <button className="btn btn-primary" onClick={e => this.exportToExcel()}>
                                <span className="fa-solid fa-file-excel" />{' '}
                                Export
                            </button>
                        }

                    </div>
                </PageHeader>

                <main>

                    <SideBar content={this.renderSideBarContent()} className="flex" />

                    <Container fluid>

                        <section>

                            {this.renderInner()}

                        </section>

                    </Container>

                </main>

            </div>
            
            <EditBookingModal ref={this.editBookingModalRef} />

        </>);
    }

    renderInner() {
        const {
            isLoading,
            isLoaded,
            bookings,
            filters
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }
        if (!isLoaded) {
            return (
                <div className="empty-text">
                    Please press 'Load bookings'.
                </div>
            );
        }

        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (
            <BookingsTable
                isScrolling
                mode="bookings"
                isATP={filters.includeATP}
                requestID={this.requestID}
                bookings={bookings}
                isEditMode={loginInfo.isAdmin}
                onClick={(booking) => {
                    this.editBooking(booking);
                }}
            />
        );
    }

    renderSideBarContent() {
        const {
            isLoadingFilters,
            isLoading,
            courses,
            filters,
            monthlyStats,
            isDirty
        } = this.state;
        const { page } = this.props.match.params;
        const loginInfo = GlobalStateService.getValue('loginInfo');
        const vendors = [{ id: '', name: '(All)' }].concat(this.state.vendors);
        const geos = [{ id: '', name: '(All)' }].concat(this.state.geos);

        if (isLoadingFilters) {
            return (<Loader />);
        }

        return (

            <form onSubmit={e => {
                e.preventDefault();
                this.load();
            }}>

                <Form.Group className="mb-3">
                    <Form.Label>Search</Form.Label>
                    <input
                        className="form-control"
                        type="search"
                        value={filters.search || ''}
                        onChange={e => this.updateFilter('search', e.target.value)}
                    />
                    <div className="alert alert-info form-group-info">
                        Search by course, customer, instructor, language, location or {filters.bookingType == 'ATP' ? 'booking reference' : 'LMS ID'}
                    </div>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Geo</Form.Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        value={geos.find(g => g.id == filters.geoID)}
                        options={geos}
                        getOptionValue={g => g.id}
                        getOptionLabel={g => g.name}
                        onChange={g => this.updateFilter('geoID', g.id)}
                        placeholder="(All)"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Vendor</Form.Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        value={vendors.find(v => v.id == filters.vendorID)}
                        options={vendors}
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.name}
                        onChange={v => this.updateFilter('vendorID', v.id)}
                        placeholder="(All)"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Date from</Form.Label>
                    <DatePicker
                        className="form-control"
                        selected={filters.dateFrom}
                        onChange={value => this.updateFilter('dateFrom', value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Date to</Form.Label>
                    <DatePicker
                        className="form-control"
                        selected={filters.dateTo}
                        onChange={value => this.updateFilter('dateTo', value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <select
                        className="form-control"
                        value={filters.status || ''}
                        onChange={e => this.updateFilter('status', e.target.value)}
                    >
                        <option value="">(Please select)</option>
                        <option value="Any">Any status</option>
                        <option value="Active">Active</option>
                        <option value="Hold">Hold</option>
                        <option value="Tentative">Tentative</option>
                        <option value="Cancelled">Cancelled</option>
                    </select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Course</Form.Label>
                    <select
                        className="form-control"
                        value={filters.courseID || ''}
                        onChange={e => this.updateFilter('courseID', e.target.value)}
                    >
                        <option value="">(All courses)</option>
                        {courses.map(c =>
                            <option key={c.id} value={c.id}>{c.name}</option>
                        )}
                    </select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Other options</Form.Label>
                    <Form.Check
                        type="checkbox"
                        id="include-shadow"
                        checked={!filters.excludeShadow}
                        onChange={e => this.updateFilter('excludeShadow', !e.target.checked)}
                        label="Include shadow bookings"
                    />
                    <Form.Check
                        type="checkbox"
                        id="include-non-atp"
                        checked={!!filters.includeNonATP}
                        onChange={e => this.updateFilter('includeNonATP', e.target.checked)}
                        label="Include non-ATP bookings"
                    />
                    <Form.Check
                        type="checkbox"
                        id="include-atp"
                        checked={!!filters.includeATP}
                        onChange={e => this.updateFilter('includeATP', e.target.checked)}
                        label="Include ATP bookings"
                    />
                    <Form.Check
                        type="checkbox"
                        id="include-archived"
                        checked={!!filters.includeArchived}
                        onChange={e => this.updateFilter('includeArchived', e.target.checked)}
                        label="Include archived bookings"
                    />
                </Form.Group>

                <button type="submit" className="btn btn-primary w-100 mt-auto" disabled={isLoading}>
                    Load bookings {isDirty ? ' *' : ''}
                </button>

                {loginInfo.isSiteAdmin && monthlyStats && <>
                    <div className="monthly-stats-container">
                        {monthlyStats.map(ms =>
                            <div className="monthly-stats" key={moment(ms.month).format('YYYY-MM')}>
                                <div className="month">
                                    {moment(ms.month).format('MMM YY')}
                                </div>
                                <div className="stat">
                                    Revenue: {TextHelpers.formatCurrencyNew(ms.revenue, { numDP: 0 })}
                                </div>
                                <div className="stat">
                                    Profit: {TextHelpers.formatCurrencyNew(ms.profit, { numDP: 0 })}
                                </div>
                            </div>
                        )}
                    </div>
                </>}

            </form>
        );
    }
}

export default withRouter(BookingsPage);